import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';

export interface VestingCategory {
  id?: string;
  category: string;
  walletAddress: string;
  cliffPeriod: number; // Duration in months (must be >= 1)
  vestingDuration: number; // Duration in months (must be >= 1)
  releasingFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  tgeUnlock: number; // Renamed from initialUnlock
  firstClaim: number; // First claim in months after TGE
}

@Component({
  selector: 'app-vesting-category-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
  ],
  templateUrl: './vesting-category-dialog.component.html',
  styleUrl: './vesting-category-dialog.component.css',
})
export class VestingCategoryDialogComponent implements OnInit {
  isEdit: boolean = false;

  // Form controls
  categoryControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  walletAddressControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^0x[a-fA-F0-9]{40}$/),
  ]);
  cliffPeriodControl = new FormControl<number>(1, [Validators.required, Validators.min(1)]);
  vestingDurationControl = new FormControl<number>(1, [Validators.required, Validators.min(1)]);
  releasingFrequencyControl = new FormControl<
    'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
  >('monthly', [Validators.required]);
  tgeUnlockControl = new FormControl(0, [
    Validators.required,
    Validators.min(0),
    Validators.max(100),
  ]);
  firstClaimValueControl = new FormControl(1, [Validators.required, Validators.min(0)]);

  vestingCategoryForm: FormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translateService: TranslateService
  ) {
    this.vestingCategoryForm = new FormGroup({
      category: this.categoryControl,
      walletAddress: this.walletAddressControl,
      cliffPeriod: this.cliffPeriodControl,
      vestingDuration: this.vestingDurationControl,
      releasingFrequency: this.releasingFrequencyControl,
      tgeUnlock: this.tgeUnlockControl,
      firstClaimValue: this.firstClaimValueControl,
    });
  }

  ngOnInit(): void {
    // Check if we're editing an existing vesting category
    if (this.config.data?.vestingCategory) {
      this.isEdit = true;
      const vestingCategory: VestingCategory = this.config.data.vestingCategory;

      this.categoryControl.setValue(vestingCategory.category);
      this.walletAddressControl.setValue(vestingCategory.walletAddress);

      // Values are now numbers (months)
      this.cliffPeriodControl.setValue(vestingCategory.cliffPeriod);
      this.vestingDurationControl.setValue(vestingCategory.vestingDuration);

      this.releasingFrequencyControl.setValue(vestingCategory.releasingFrequency);
      this.tgeUnlockControl.setValue(vestingCategory.tgeUnlock);

      // firstClaim is now just a number (months after TGE)
      if (vestingCategory.firstClaim !== undefined) {
        this.firstClaimValueControl.setValue(vestingCategory.firstClaim);
      }
    }
  }

  private markFormGroupTouched(): void {
    Object.values(this.vestingCategoryForm.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  onSubmit(): void {
    if (this.vestingCategoryForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const vestingCategoryData: VestingCategory = {
      category: this.categoryControl.value!,
      walletAddress: this.walletAddressControl.value!,
      cliffPeriod: this.cliffPeriodControl.value!,
      vestingDuration: this.vestingDurationControl.value!,
      releasingFrequency: this.releasingFrequencyControl.value!,
      tgeUnlock: this.tgeUnlockControl.value!,
      firstClaim: this.firstClaimValueControl.value!, // Send as number (months)
    };

    if (this.isEdit && this.config.data?.vestingCategory?.id) {
      vestingCategoryData.id = this.config.data.vestingCategory.id;
    }

    this.ref.close(vestingCategoryData);
  }

  onCancel(): void {
    this.ref.close();
  }

  // Error message methods
  getCategoryErrorMessage(): string {
    if (this.categoryControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.vestingCategoryRequired');
    }
    if (this.categoryControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.vestingCategoryMinLength');
    }
    return '';
  }

  getWalletAddressErrorMessage(): string {
    if (this.walletAddressControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.vestingCategories.walletAddressRequired');
    }
    if (this.walletAddressControl.errors?.['pattern']) {
      return this.translateService.instant('projectEdit.vestingCategories.walletAddressInvalid');
    }
    return '';
  }

  getCliffPeriodErrorMessage(): string {
    if (this.cliffPeriodControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.cliffPeriodRequired');
    }
    if (this.cliffPeriodControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.cliffPeriodMin');
    }
    return '';
  }

  getVestingDurationErrorMessage(): string {
    if (this.vestingDurationControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.vestingDurationRequired');
    }
    if (this.vestingDurationControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.vestingDurationMin');
    }
    return '';
  }

  getReleasingFrequencyErrorMessage(): string {
    if (this.releasingFrequencyControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.releasingFrequencyRequired');
    }
    if (this.releasingFrequencyControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.releasingFrequencyMinLength');
    }
    return '';
  }

  getTgeUnlockErrorMessage(): string {
    if (this.tgeUnlockControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockRequired');
    }
    if (this.tgeUnlockControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockMin');
    }
    if (this.tgeUnlockControl.errors?.['max']) {
      return this.translateService.instant('projectEdit.errors.initialUnlockMax');
    }
    return '';
  }

  getFirstClaimErrorMessage(): string {
    if (this.firstClaimValueControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.vestingCategories.firstClaimRequired');
    }
    if (this.firstClaimValueControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.vestingCategories.firstClaimMin');
    }
    return '';
  }
}
